import { useReadContract } from "wagmi";
import type { Abi, Address } from "viem";

import oftAbi from "@abi/common/OFT.json";
import { oftBridgePolicy } from "@config/oftBridgeConfig";
import { buildSendParam, type OftRoute } from "@lib/oftBridge";

type UseOftBridgeQuoteArgs = {
  route: OftRoute | null;
  /** Dust-rounded amount. Quoting a different amount than we send would mislead the user. */
  amountLD: bigint;
  recipient?: Address;
};

export type OftBridgeQuote = {
  /** Fee in the SOURCE chain's native token. Null while unavailable. */
  nativeFee: bigint | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

const useOftBridgeQuote = ({
  route,
  amountLD,
  recipient,
}: UseOftBridgeQuoteArgs): OftBridgeQuote => {
  const enabled = Boolean(route && recipient && amountLD > 0n);

  const { data, isLoading, isError, refetch } = useReadContract({
    address: route?.sourceContract,
    abi: oftAbi as Abi,
    functionName: "quoteSend",
    chainId: route?.sourceChainId,
    args:
      route && recipient
        ? [buildSendParam({ dstEid: route.dstEid, recipient, amountLD }), false]
        : undefined,
    query: {
      enabled,
      refetchInterval: oftBridgePolicy.quoteRefetchMs,
    },
  });

  const fee = data as { nativeFee: bigint; lzTokenFee: bigint } | undefined;

  return {
    nativeFee: fee?.nativeFee ?? null,
    isLoading: enabled && isLoading,
    isError: enabled && isError,
    refetch: () => {
      void refetch();
    },
  };
};

export default useOftBridgeQuote;
